// @flow
import {
  ArrowLeft,
  Calculator,
  CreditCard,
  Download,
  History,
  ListChecks,
  Map,
  MapPin,
  MessageSquare,
  Pencil,
  Pin,
  Plane,
  Receipt,
  ShoppingBag,
  User,
  Wallet
} from 'lucide-react';
import Button from 'shared/components/Button';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';

import PageContainer from 'layout/components/PageContainer';
import PageHeader from 'layout/components/PageHeader';
import { ReactNode, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Profile from 'shared/components/Profile';
import LocationDetails from '../components/LocationDetails';
import { MapaRecorridoComparativaGoogle } from '../components/MapaRecorridoComparativaGoogle';
import SendReceiptModal from '../components/SendReceiptModal';
import { ViajeTrasladoModificacionesTable } from '../components/ViajeTrasladoModificacionesTable';
import { ViajeTrasladoStatesTable } from '../components/ViajeTrasladoStatesTable';
import { ViajeTrasladoSemaphoreTimeline } from '../components/ViajeTrasladoSemaphoreTimeline';
import { ViajeTrasladoExpensesType } from '../models/ViajeTrasladoExpenses';
import useViajesTraslado from '../services/ViajeTrasladoService';
import { isTransferReservationPrivate } from '../services/isTransferReservationPrivate';
import { downloadRecipiet } from '../services/recipietService';
import { TipoServicioLabel } from '../models';
import ViajeTrasladoMedioDePagoDetail from '../components/ViajeTrasladoMedioDePagoDetail';
import { ClientType } from 'app/client/main/models/ClientType';
import { ViajeCanalesDeIngreso } from 'app/viaje/models';
import {
  FieldItem,
  ObservationItem,
  Pill,
  SectionCardHeader,
  sectionCardClass
} from '../components/detailUi';

function PriceMetaRow({
  rateType,
  fixedPriceName,
  totals
}: {
  rateType: string;
  fixedPriceName?: string | null;
  totals: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Pill>{rateType}</Pill>
      {fixedPriceName ? <Pill color="blue">{fixedPriceName}</Pill> : null}
      <div className="ml-auto flex items-center gap-2">{totals}</div>
    </div>
  );
}

function PriceBreakdownRow({
  label,
  price,
  extra
}: {
  label: string;
  price: number | string | undefined;
  extra?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 text-sm font-medium text-ink dark:text-ink-dark">
        {label}
      </span>
      <Pill color="green">${price}</Pill>
      {extra}
    </div>
  );
}

export function ViajeTrasladoDetailPage() {
  const history = useHistory();
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const viajeTrasladoRepository = useViajesTraslado();
  /*  const settlementRepo = useDriverSettlementRepository(); */
  const [isLoadingRecipiet, setIsLoadingRecipiet] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<
    | 'personal'
    | 'general'
    | 'ubicaciones'
    | 'recorrido'
    | 'importes'
    | 'modificaciones'
    | 'medioDePago'
  >('general');

  const viajeQuery = useSuperQuery(
    () => viajeTrasladoRepository.get(Number.parseInt(id)),
    { showSpinner: true }
  );

  const downloadVoucher = () => {
    setIsLoadingRecipiet(true);
    return downloadRecipiet(id)
      .then((data: any) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Voucher-ID-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => alert(error))
      .finally(() => {
        setIsLoadingRecipiet(false);
      });
  };

  /*   const settlementResumeQuery = useQuery(['settlement_resume'], () =>
    settlementRepo.getDriverSettlementDetail(id, viajeQuery.data?.assigned_driver?.codigo!), {
      enabled: !!viajeQuery.data?.id
    }
  ); */

  const geolocationsQuery = useSuperQuery(() =>
    viajeTrasladoRepository.getRoute(Number.parseInt(id))
  );
  const expensesQuery = useSuperQuery(() =>
    viajeTrasladoRepository.getExpenses(Number.parseInt(id))
  );
  const modificacionesQuery = useSuperQuery(() =>
    viajeTrasladoRepository.getModificaciones(Number(id))
  );

  if (!viajeQuery.data) {
    return null;
  }

  const locations = viajeQuery.data.locations.map((p) => ({
    address: p.address,
    observation: p.observation ?? '',
    passengers: p.passengers.map((pa) => ({
      name: pa.name,
      phone: pa.phone ? pa.phone.toString() : ''
    }))
  }));

  const allowedClientIds = [2030];

  let coordinationObservationValue = '';
  if (typeof viajeQuery.data.coordinationObservation === 'string') {
    coordinationObservationValue = viajeQuery.data.coordinationObservation;
  } else if (viajeQuery.data.coordinationObservation !== null && viajeQuery.data.coordinationObservation !== undefined) {
    coordinationObservationValue = String(viajeQuery.data.coordinationObservation);
  }

  const resolveRateType = (rateType: string) =>
    rateType in TipoServicioLabel ? TipoServicioLabel[`${rateType}`] : rateType;

  const steps = {
    general: (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {isTransferReservationPrivate(viajeQuery.data) ? null : (
            <div className={sectionCardClass}>
              <SectionCardHeader icon={User} title="Solicitante" />
              <div className="flex flex-col gap-3">
                <FieldItem
                  label="Nombre"
                  value={viajeQuery.data.requirements?.applicant?.name ?? '—'}
                />
                <FieldItem
                  label="Telefono"
                  value={viajeQuery.data.requirements?.applicant?.phone ?? '—'}
                />
                <FieldItem
                  label="Mail"
                  value={viajeQuery.data.requirements?.applicant?.email ?? '—'}
                />
              </div>
            </div>
          )}
          <div className={sectionCardClass}>
            <SectionCardHeader icon={ListChecks} title="Detalles" />
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink dark:text-ink-dark">
                  Tiene prioridad
                </span>
                <Pill color={viajeQuery.data.hasPriority ? 'green' : 'red'}>
                  {viajeQuery.data.hasPriority ? 'Si' : 'No'}
                </Pill>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink dark:text-ink-dark">
                  Tiene Out Site
                </span>
                <Pill color={viajeQuery.data.has_out_site ? 'green' : 'red'}>
                  {viajeQuery.data.has_out_site ? 'Si' : 'No'}
                </Pill>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink dark:text-ink-dark">
                  Es evento
                </span>
                <Pill color={viajeQuery.data.is_event ? 'green' : 'red'}>
                  {viajeQuery.data.is_event ? 'Si' : 'No'}
                </Pill>
              </div>
              {viajeQuery.data.is_event ? (
                <FieldItem
                  label="Nombre del evento"
                  value={viajeQuery.data.event_name}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className={sectionCardClass}>
            <SectionCardHeader icon={MessageSquare} title="Observaciones" />
            <div className="flex flex-col gap-4">
              <ObservationItem
                label="Coordinación"
                text={coordinationObservationValue || '—'}
              />
              <ObservationItem
                label="LLT País"
                text={viajeQuery?.data?.lltpais?.observation_lltpais ?? 'No'}
              />
              <ObservationItem
                label="Usuario App"
                text={viajeQuery.data.general_observation || '—'}
              />
              {viajeQuery?.data?.reason_reserve_past ? (
                <ObservationItem
                  label="Creación en el pasado"
                  text={viajeQuery.data.reason_reserve_past}
                />
              ) : null}
            </div>
          </div>

          {viajeQuery?.data?.airport_details ? (
            <div className={sectionCardClass}>
              <SectionCardHeader icon={Plane} title="Información de Vuelo" />
              <div className="flex flex-col gap-3">
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-ink dark:text-ink-dark">
                    {viajeQuery.data.airport_details?.name_aeroline ?? '-'}
                  </p>
                  <p className="text-xs text-inkMuted dark:text-inkMuted-dark">
                    {viajeQuery.data.airport_details?.number_flight ?? '-'}
                  </p>
                </div>
                <div className="border-t border-line dark:border-white/10" />
                <div className="flex flex-col gap-3">
                  {viajeQuery.data.airport_details?.items?.map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between"
                    >
                      <div className="flex flex-col">
                        <p className="text-sm text-ink dark:text-ink-dark">
                          {item.name}
                        </p>
                        <p className="text-xs text-inkMuted dark:text-inkMuted-dark">
                          {item.description}
                        </p>
                      </div>
                      <p className="text-base font-medium text-ink dark:text-ink-dark">
                        x{item.quantity}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          {allowedClientIds.includes(viajeQuery.data.client.id) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsInvoiceModalOpen(true)}
            >
              Enviar Recibo
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={Download}
            isLoading={isLoadingRecipiet}
            onClick={() => downloadVoucher()}
          >
            Descargar Recibo
          </Button>
        </div>
      </div>
    ),
    ubicaciones: (
      <div className={sectionCardClass}>
        <SectionCardHeader icon={MapPin} title="Ubicaciones" />
        <LocationDetails locations={locations} />
      </div>
    ),
    recorrido: (
      <div className={`flex h-full w-full flex-col ${sectionCardClass}`}>
        <SectionCardHeader icon={Map} title="Recorrido" />
        <div className="h-5/6 w-full">
          <MapaRecorridoComparativaGoogle
            recorrido={(geolocationsQuery.data ?? []).map((pos) => ({
              lat: pos.latitude,
              lng: pos.longitude
            }))}
            recorridoGoogle={{
              datetime: viajeQuery.data.departureDatetime,
              puntos: viajeQuery.data.locations.map((pos) => ({
                lat: pos.address.latitud,
                lng: pos.address.longitud
              }))
            }}
          />
        </div>
      </div>
    ),
    importes: (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {viajeQuery.data.estimated_prices ? (
            <div className={sectionCardClass}>
              <SectionCardHeader icon={Calculator} title="Estimados" />
              <div className="flex flex-col gap-4">
                <PriceMetaRow
                  rateType={resolveRateType(
                    viajeQuery.data.estimated_prices.rate_type
                  )}
                  fixedPriceName={
                    viajeQuery.data.estimated_prices.fixed_price_name
                  }
                  totals={
                    <Pill color="green" size="md">
                      ${viajeQuery.data.estimated_prices.transfer_price}
                    </Pill>
                  }
                />
                <div className="grid grid-cols-2 gap-3">
                  <PriceBreakdownRow
                    label="Kilometros:"
                    price={viajeQuery.data.estimated_prices.km_price}
                    extra={
                      <Pill color="gray">
                        {viajeQuery.data.estimated_prices.kilometers} km.
                      </Pill>
                    }
                  />
                  <PriceBreakdownRow
                    label="Espera:"
                    price={
                      viajeQuery.data.estimated_prices.waiting_minutes_price
                    }
                    extra={
                      <Pill color="gray">
                        {viajeQuery.data.estimated_prices.waiting_minutes} m.
                      </Pill>
                    }
                  />
                  <PriceBreakdownRow
                    label="Out-site:"
                    price={viajeQuery.data.estimated_prices.out_site_price}
                  />
                  <PriceBreakdownRow
                    label="Night-plus:"
                    price={viajeQuery.data.estimated_prices.night_plus_price}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className={sectionCardClass}>
            <SectionCardHeader icon={Wallet} title="Realizados" />
            {!viajeQuery.data.calculated_prices ? (
              <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
                Aún no se ha realizado el viaje
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                <PriceMetaRow
                  rateType={resolveRateType(
                    viajeQuery.data.calculated_prices.rate_type
                  )}
                  fixedPriceName={
                    viajeQuery.data.calculated_prices.fixed_price_name
                  }
                  totals={
                    <Pill color="green" size="md">
                      ${viajeQuery.data.calculated_prices.transfer_price}
                    </Pill>
                  }
                />
                <div className="grid grid-cols-2 gap-3">
                  <PriceBreakdownRow
                    label="Kilometros:"
                    price={viajeQuery.data.calculated_prices.km_price}
                    extra={
                      <Pill color="gray">
                        {viajeQuery.data.calculated_prices.kilometers} km.
                      </Pill>
                    }
                  />
                  <PriceBreakdownRow
                    label="Espera:"
                    price={
                      viajeQuery.data.calculated_prices.waiting_minutes_price
                    }
                    extra={
                      <Pill color="gray">
                        {viajeQuery.data.calculated_prices.waiting_minutes} m.
                      </Pill>
                    }
                  />
                  <PriceBreakdownRow
                    label="Out-site:"
                    price={viajeQuery.data.calculated_prices.out_site_price}
                  />
                  <PriceBreakdownRow
                    label="Plus-nocturno:"
                    price={viajeQuery.data.calculated_prices.night_plus_price}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {viajeQuery.data.calculated_prices ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={sectionCardClass}>
              <SectionCardHeader icon={Receipt} title="Precio Final" />
              {!viajeQuery.data.final_prices ? (
                <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
                  Aún no hay datos finales
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  <PriceMetaRow
                    rateType={resolveRateType(
                      viajeQuery.data.final_prices.rate_type
                    )}
                    fixedPriceName={
                      viajeQuery.data.final_prices.fixed_price_name
                    }
                    totals={
                      <>
                        <Pill color="green" size="md">
                          ${viajeQuery.data.final_prices.transfer_price}
                        </Pill>
                        <Pill color="red" size="md">
                          ${viajeQuery.data.final_prices.driver_price}
                        </Pill>
                      </>
                    }
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <PriceBreakdownRow
                      label="Kilometros:"
                      price={viajeQuery.data.final_prices.km_price}
                      extra={
                        <Pill color="gray">
                          {viajeQuery.data.final_prices.kilometers} km.
                        </Pill>
                      }
                    />
                    <PriceBreakdownRow
                      label="Espera:"
                      price={viajeQuery.data.final_prices.waiting_minutes_price}
                      extra={
                        <Pill color="gray">
                          {viajeQuery.data.final_prices.waiting_minutes} m.
                        </Pill>
                      }
                    />
                    <PriceBreakdownRow
                      label="Out-site:"
                      price={viajeQuery.data.final_prices.out_site_price}
                    />
                    <PriceBreakdownRow
                      label="Plus-nocturno:"
                      price={viajeQuery.data.final_prices.night_plus_price}
                    />
                    <PriceBreakdownRow
                      label="Gastos:"
                      price={viajeQuery.data.final_prices?.expenses_price}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={sectionCardClass}>
              <SectionCardHeader icon={ShoppingBag} title="Gastos y Peajes" />
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-2 text-sm font-semibold text-ink dark:text-ink-dark">
                    Gastos
                  </p>
                  <div className="flex flex-col gap-2">
                    {expensesQuery.data
                      ?.filter(
                        (e) => e.type === ViajeTrasladoExpensesType.GASTO
                      )
                      .map((e, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap items-center gap-4 text-sm text-inkMuted dark:text-inkMuted-dark"
                        >
                          <span>
                            <span className="font-medium text-ink dark:text-ink-dark">
                              Nombre:
                            </span>{' '}
                            {e.name}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-ink dark:text-ink-dark">
                              Precio:
                            </span>
                            <Pill color="red">${e.price}</Pill>
                          </span>
                          <a
                            href={e.photo!}
                            target="_blank"
                            rel="noreferrer"
                            className="text-secondary-600 hover:underline dark:text-secondary-300"
                          >
                            Ver Imagen
                          </a>
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-ink dark:text-ink-dark">
                    Peajes
                  </p>
                  <div className="flex flex-col gap-2">
                    {expensesQuery.data
                      ?.filter(
                        (e) => e.type === ViajeTrasladoExpensesType.PEAJE
                      )
                      .map((e, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap items-center gap-4 text-sm text-inkMuted dark:text-inkMuted-dark"
                        >
                          <span>
                            <span className="font-medium text-ink dark:text-ink-dark">
                              Nombre:
                            </span>{' '}
                            {e.name}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-ink dark:text-ink-dark">
                              Precio:
                            </span>
                            <Pill color="red">${e.price}</Pill>
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
                {viajeQuery.data.deviation_observation ? (
                  <ObservationItem
                    label="Observación Desvío"
                    text={viajeQuery.data.deviation_observation}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    ),
    ...(viajeQuery.data.clientType !== ClientType.business && viajeQuery.data.entryChannel === ViajeCanalesDeIngreso.APP && {
      medioDePago: (
        <ViajeTrasladoMedioDePagoDetail
          payment={isTransferReservationPrivate(viajeQuery.data) ? viajeQuery.data.payment : undefined}
        />
      )
    }),
    estados: (
      <div className="flex flex-col gap-6">
        <div className={sectionCardClass}>
          <SectionCardHeader icon={History} title="Historial de Estados" />
          <ViajeTrasladoStatesTable
            reservationStates={viajeQuery.data.states}
          />
        </div>
        {viajeQuery.data.semaphore?.rules?.length ? (
          <div className={sectionCardClass}>
            <SectionCardHeader icon={Pin} title="Semáforo" />
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-ink dark:text-ink-dark">
                  {viajeQuery.data.semaphore.group_name}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    history.push(
                      `/main/inventories/traffic-lights-management/traffic-light-rules-configuration/${viajeQuery.data!.semaphore!.group_id}?readOnly=true`
                    )
                  }
                >
                  Ver regla
                </Button>
              </div>
              <ViajeTrasladoSemaphoreTimeline
                rules={viajeQuery.data.semaphore.rules}
              />
            </div>
          </div>
        ) : null}
      </div>
    ),
    modificaciones: (
      <div className={sectionCardClass}>
        <SectionCardHeader icon={Pencil} title="Modificaciones" />
        <ViajeTrasladoModificacionesTable
          data={modificacionesQuery.data ?? []}
        />
      </div>
    )
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <PageHeader
        title="Traslados"
        subArea={
          <div className="flex space-x-2">
            <span
              className="cursor-pointer text-xs text-inkMuted hover:underline dark:text-inkMuted-dark"
              onClick={history.goBack}
            >
              Traslados
            </span>
            <span className="text-inkMuted dark:text-inkMuted-dark">/</span>
            <span className="text-xs text-inkMuted dark:text-inkMuted-dark">
              {viajeQuery.data.client.name}
            </span>
          </div>
        }
      />
      <PageContainer lessMargin>
        <div className="flex w-full flex-col">
          <Button
            variant="secondary"
            size="sm"
            icon={ArrowLeft}
            className="mb-6 self-start"
            onClick={history.goBack}
          >
            Volver
          </Button>
          <Profile.Container
            navigation={
              <Profile.Navigation
                image={
                  <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl bg-secondary-100 dark:bg-white/10">
                    <h1 className="text-2xl font-bold text-secondary-700 dark:text-secondary-200">
                      {viajeQuery.data.client.name
                        .split(' ')
                        .map((name) => name[0])
                        .join('')
                        .toUpperCase()}
                    </h1>
                  </div>
                }
                resume={
                  <Profile.Resume.Container>
                    <Profile.Resume.Title>
                      {viajeQuery.data.client.name}
                    </Profile.Resume.Title>
                    <Profile.Resume.Subtitle>
                      {viajeQuery.data.reservationDatetime.format(
                        'DD/MM/YYYY HH:mm'
                      )}
                    </Profile.Resume.Subtitle>
                    <Profile.Resume.Badge color="green">
                      {viajeQuery.data.state}
                    </Profile.Resume.Badge>
                  </Profile.Resume.Container>
                }
                description={
                  <Profile.Description.Container>
                    <Profile.Description.Item
                      label="ID"
                      value={viajeQuery.data.id}
                    />
                    <Profile.Description.Item
                      label="Servicio"
                      value={
                        viajeQuery.data.serviceType in TipoServicioLabel
                          ? TipoServicioLabel[`${viajeQuery.data.serviceType}`]
                          : viajeQuery.data.serviceType
                      }
                    />
                    <Profile.Description.Item
                      label="Canal de ingreso"
                      value={viajeQuery.data.entryChannel}
                    />
                    <Profile.Description.Item
                      label="Chofer"
                      value={
                        viajeQuery.data.assigned_driver
                          ? '#' +
                            viajeQuery.data.assigned_driver.codigo +
                            ' - ' +
                            viajeQuery.data.assigned_driver.nombreCompleto
                          : 'Sin asignar'
                      }
                    />
                  </Profile.Description.Container>
                }
                menu={
                  <Profile.Menu.Container
                    value={currentStep}
                    onSelect={setCurrentStep}
                  >
                    <Profile.Menu.Item
                      value="general"
                      label="General"
                      icon={<User size={16} />}
                    />
                    <Profile.Menu.Item
                      value="ubicaciones"
                      label="Ubicaciones"
                      icon={<MapPin size={16} />}
                    />
                    <Profile.Menu.Item
                      value="recorrido"
                      label="Recorrido"
                      icon={<Map size={16} />}
                    />
                    <Profile.Menu.Item
                      value="importes"
                      label="Importes"
                      icon={<Wallet size={16} />}
                    />
                 {viajeQuery.data.clientType !== ClientType.business && viajeQuery.data.entryChannel === ViajeCanalesDeIngreso.APP && <Profile.Menu.Item
                      value="medioDePago"
                      label="Medio de pago"
                      icon={<CreditCard size={16} />}
                    />}
                    <Profile.Menu.Item
                      value="estados"
                      label="Estados"
                      icon={<Pin size={16} />}
                    />
                    <Profile.Menu.Item
                      value="modificaciones"
                      label="Modificaciones"
                      icon={<Pencil size={16} />}
                    />
                  </Profile.Menu.Container>
                }
              />
            }
            body={
              <>
                {currentStep !== 'recorrido' && steps[currentStep]}
                {/* h-full (no flex-1): el padre de este div es el wrapper de
                    Profile.Container, que no es flex; h-full se resuelve contra
                    la columna estirada y le da alto definido al mapa. */}
                <div
                  className={currentStep === 'recorrido' ? 'h-full' : 'hidden'}
                >
                  {steps.recorrido}
                </div>
              </>
            }
          />
        </div>
      </PageContainer>
      {isInvoiceModalOpen && (
        <SendReceiptModal
          clientId={viajeQuery.data.client.id}
          onClose={() => setIsInvoiceModalOpen(false)}
          reserveId={id}
        />
      )}
      {/*       <div className="my-4 flex items-start gap-8 px-4">
        <Card className="flex w-1/3 flex-shrink-0 justify-center p-8">
            {settlementResumeQuery.data === undefined ? null : (
              <DriverSettlementDetailResumeComponent
                settlement={settlementResumeQuery.data}
              />
            )}
        </Card>
      </div> */}
    </div>
  );
}
